// The "Paste what you have" card in words and rules, pure (no React) so
// proposalModel.selftest.ts runs it on its own: what kind of thing the
// proposal is ("Python library", "API with a key", a product's own name),
// a fact as the card shows it (a secret reads "from your paste"), a
// question as its input's label (an optional one ends "(if it needs one)"),
// whether Add can be pressed, whether the box changed since the proposal
// was made, the card's ONE line (a note's words, else what Add will try,
// else what it is), what each refusal says, and whether a workspace offers
// the card at all.
//
// The server decides everything the card says about the paste (facts,
// questions, where a key goes, what Add tries); these rules only choose
// which of its words to show and when. Also here: when the card stops
// reading a running proposal (a 410 or a 404 ends the poll), what the
// button for an already added one says, and which paste screen the Data
// dialog shows.

import type { ProposalField, ProposalOut, ProposalQuestion, ProposeWhere } from "@/api/connectProposals";

/** A data source's kind, as the card's head says it (the connections list's words, P2: never "Data API" or "Dataset"). */
const KIND_WORDS: Record<string, string> = {
  python: "Python library",
  postgres: "PostgreSQL database",
  mysql: "MySQL database",
  clickhouse: "ClickHouse database",
  url: "Link to a file",
  seafile: "Seafile file server",
  http_api: "API",
};

/** A generic connector, as the card's head says it (a product says its own name; a keyed API is an API, P2). */
const CONNECTOR_WORDS: Record<string, string> = {
  "mcp-url": "MCP server",
  "rest-key": "API",
  "openapi-url": "API",
};

export const SECRET_VALUE = "from your paste";
export const OPTIONAL_SUFFIX = " (if it needs one)";
export const STALE_LINE = "You changed the text: work it out again.";
export const EXPIRED_LINE = "This was a while ago: work it out again.";
export const NO_TARGET_LINE = "Corvus could not tell what this is. Say what it is: its web address, or the library's name.";
export const UNAVAILABLE_PREFIX = "Read from the text only (Corvus's model did not answer). ";
/** While the first answer is on its way (the job's own first step). */
export const FIRST_STEP = "Reading what you pasted…";

/** The kind word beside the title: "Python library", "MCP server", "GitLab"; "" when there is nothing to add. */
export function kindWord(p: Pick<ProposalOut, "target" | "kind" | "connector" | "connector_name">): string {
  if (p.target === "data_source") return (p.kind && KIND_WORDS[p.kind]) || "";
  if (p.target === "connection") return (p.connector && CONNECTOR_WORDS[p.connector]) || p.connector_name || p.connector || "";
  return "";
}

/** One fact as the card shows it: its label and its value, a secret as "from your paste". */
export function factLine(f: ProposalField): { label: string; value: string } {
  if (f.secret) return { label: f.label, value: SECRET_VALUE };
  return { label: f.label, value: (f.value ?? "").trim() };
}

/** A question as its input's label: the question itself, an optional one ending "(if it needs one)". */
export function questionLabel(q: Pick<ProposalQuestion, "ask" | "required">): string {
  const ask = q.ask.trim();
  return q.required ? ask : `${ask}${OPTIONAL_SUFFIX}`;
}

/** The box no longer says what the proposal was made from. */
export function isStale(boxText: string, madeFrom: string): boolean {
  return boxText.trim() !== madeFrom.trim();
}

/** There is something to add: a target, nothing in the way, not added yet. */
export function addable(p: ProposalOut | null | undefined): boolean {
  if (!p || p.state !== "ready" || !p.target || p.added) return false;
  return !(p.already || p.elsewhere || p.unsupported || p.refused || p.needs_host);
}

/** Add it can be pressed: something to add, the box unchanged, every required question answered. */
export function canAdd(p: ProposalOut | null | undefined, answers: Readonly<Record<string, string>>, stale = false): boolean {
  if (stale || !p || !addable(p)) return false;
  return (p.questions ?? []).every((q) => !q.required || (answers[q.field] ?? "").trim() !== "");
}

/** What confirm is sent: the answers to this proposal's own questions, blanks left out (a secret as typed, the rest trimmed). */
export function answerBody(p: Pick<ProposalOut, "questions"> | null | undefined, answers: Readonly<Record<string, string>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of p?.questions ?? []) {
    const raw = answers[q.field] ?? "";
    if (!raw.trim()) continue;
    out[q.field] = q.secret ? raw : raw.trim();
  }
  return out;
}

/**
 * The card's one line: a note's words (already added, belongs elsewhere,
 * not something Corvus connects to, refused), "Corvus could not tell" when
 * there is no target, else what Add will try (said as read from the text
 * only when the model did not answer), else what it is.
 */
export function oneLine(p: ProposalOut): string {
  const note = p.already ?? p.elsewhere ?? p.unsupported;
  if (note?.words) return note.words.trim();
  if (p.refused) return p.refused.trim();
  if (!p.target) return p.explanation.trim() || NO_TARGET_LINE;
  if (p.will_try) return `${p.model === "unavailable" ? UNAVAILABLE_PREFIX : ""}${p.will_try.trim()}`;
  return p.explanation.trim();
}

/**
 * What a refusal of the proposal routes says, in words; null for a code
 * they do not own (a connection's or a data source's own refusal, said by
 * its own words). `message` is the server's sentence, for the codes whose
 * words are the server's ("test_failed": why the test failed).
 */
export function errorWords(code: string | null | undefined, message = ""): string | null {
  const said = message.trim();
  switch (code) {
    case "busy":
      return "Corvus is already working out two connections: try again in a minute.";
    case "over_limit":
      return "Too many at once: try again in a minute.";
    case "propose_failed":
      return "Corvus could not work this out just now: try again.";
    case "too_long":
      return "That is too long: paste the part about the connection.";
    case "empty":
      return "Paste something first.";
    case "expired":
      return EXPIRED_LINE;
    case "still_missing":
      return "Answer the questions first.";
    case "bad_answer":
      return said || "That answer is too long or not one line.";
    case "test_failed":
      return said || "It did not work.";
    case "not_ready":
      return "Corvus is still working it out.";
    case "nothing_to_add":
      return "There is nothing to add here.";
    case "confirming":
      return "It is being added.";
    case "already_added":
      return "It is already added.";
    case "propose_off":
      // The dialog goes back to its own flow once the list says so.
      return "";
    default:
      return null;
  }
}

/** How often a running proposal is read again. */
export const POLL_MS = 1_500;

/**
 * A poll's answer that ends it: 410 (expired, or Corvus restarted and lost
 * it) or 404 (proposals switched off). Anything else may pass: read again.
 */
export function pollEnded(status: number | null | undefined): boolean {
  return status === 404 || status === 410;
}

/** When to read a proposal again: every 1.5 s while its last answer says running, never once the poll has ended. */
export function pollEvery(state: ProposalOut["state"] | undefined, errorStatus: number | null | undefined): number | false {
  return state === "running" && !pollEnded(errorStatus) ? POLL_MS : false;
}

/** Still being worked out: a send on its way, or the last answer says running and the poll has not ended. */
export function stillRunning(sending: boolean, state: ProposalOut["state"] | undefined, errorStatus: number | null | undefined): boolean {
  return sending || (state === "running" && !pollEnded(errorStatus));
}

/** Said instead of "Use it" when a picker cannot hand the service back (only Settings gives it). */
export const GIVE_IN_SETTINGS = "Give it from Settings › Connections.";

/**
 * The button for something already added. Settings opens its row; the
 * Data dialog hands the source to whoever opened it (a subagent, a
 * workflow, a draft), so it says so.
 */
export function existingLabel(where: ProposeWhere, note?: { words?: string; entry?: string | null } | null): string {
  if (where === "data") return "Use it";
  // the connections list's entry (P2: "EODHD is already connected, with this key."): open it by its name
  const named = note?.entry ? /^(.{1,40}?) is already connected\b/.exec(note.words ?? "") : null;
  return named ? `Open ${named[1]}` : "Open";
}

/**
 * The Data dialog's paste screen. Corvus's box where the workspace offers
 * proposals, unless today's paste was typed into before the list said so
 * (`paneKept`: that text stays where it was typed). While the form or the
 * kind picker shows, the box stays mounted, hidden, so Back finds its text
 * and its card as they were.
 */
export function pasteScreen(s: { pasting: boolean; proposals: boolean; paneKept: boolean }): { box: "shown" | "hidden" | null; pane: boolean } {
  const box = s.proposals && !s.paneKept;
  return { box: box ? (s.pasting ? "shown" : "hidden") : null, pane: s.pasting && !box };
}

/** Today's paste is kept for this opening once it was typed into while the box was not offered. */
export function keepsPane(kept: boolean, proposals: boolean, paneText: string): boolean {
  return kept || (!proposals && paneText.trim() !== "");
}

/** Whether Add offers "Work it out": the workspace is on Connections and the api offers proposals (an older api: no). */
export function proposalsOn(list: { enabled?: boolean; proposals?: boolean } | null | undefined): boolean {
  return list?.enabled === true && list?.proposals === true;
}
